import * as React from 'react'
import { ReactSortable } from 'react-sortablejs'
import embedLoadingIcon from '../../../../assets/icon.png'
import embedUosLogo from '../../../../assets/uos.png'
import { MapView, ModelOutputCache } from '../map/map_view'
import './embed_project_editor.css'
import { Project, defaultProject, Layer } from '../state'
import { Extent } from 'ol/extent'
import { DBModels } from '../state/db_models'
import { ProjectPermissions, TeamExtentData, SnapshotExtentData, TeamComponentData } from './project_editor'
import { DatasetCache } from '../map/map_view'
import { iconForLayerType } from '../utils/util'
import { ModelView } from './model_view'
import { getExpressions, getDatasets, getAnnotations, saveModelOutput, getDataset } from '../state/saved_dataset'
import { calculateProjectExtent, calculateProjectZoom, calculateProjectCenter, extractProjectMaskSettings } from '../utils/project_utils'
import { reconcileMapStackWithProject, projectOrderFromStack, isSameLayerIdMultiset, slotToKey, keyToSlot, type MapStackSlot } from '../map/map_layer_stack'
import type { LocalGeotiffLayerState } from '../map/local_geotiff_preview'
import { ModelOutputLayer, DatasetLayer } from '../state'
import { getUrlParam, getUrlParamAsBoolean, getUrlParamAsNumber } from '../utils/url_params'
import { AnalysisPanel } from '../snapshot/analysis_panel'
import { Tab } from './project_editor'
import { CollapsedSidebar, LayerSettingsHeader } from './sidebar'
import { getColorStops } from '../reify_layer/model_output'
import {
  applyCategoricalColorMutation,
  labelEntriesFromStatsLabels,
  resolveCategoricalPalette,
} from '../layers/categorical_layer_colors'
import { effectiveLegendStatsForLayer } from '../layers/categorical_layer_view'
import { Data } from 'rete/types/core/data'
import { LayerListRow } from '../../shared/layer_list_row'

/** Match snapshot (analysis) column to layers sidebar width */
const EMBED_SIDE_COLUMN_WIDTH_PX = 248

interface EmbedProjectEditorProps {
  projectSource: Project
  dbModels: DBModels
  teamId: number
  teamName: string
  teamComponents: TeamComponentData[]
  permissions: ProjectPermissions
  teamExtents: TeamExtentData[]
}

export function EmbedProjectEditor({ projectSource, dbModels, teamId, teamName, teamComponents, permissions, teamExtents }: EmbedProjectEditorProps) {
  // Read URL parameters for embed configuration
  // Note: getUrlParamAsBoolean returns false if param is not present, so we check for explicit 'false'
  const sidebarParam = getUrlParam('sidebar')
  const urlSidebar = sidebarParam === null ? true : sidebarParam.toLowerCase() === 'true'
  const urlZoom = getUrlParamAsNumber('zoom')
  const urlCenter = getUrlParam('center') // Format: "lat,lng" or "x,y"
  const urlDebug = getUrlParamAsBoolean('debug')
  const autostartParam = getUrlParam('autostart')
  const urlAutostart = autostartParam === null ? true : autostartParam.toLowerCase() === 'true'
  const urlInitDelay = getUrlParamAsNumber('initDelay') ?? 2000 // Default 2000ms
  const urlTimeout = getUrlParamAsNumber('timeout') ?? 60000 // Default 60 seconds
  const snapshotParam = getUrlParam('snapshot')
  const urlSnapshot = snapshotParam !== null && snapshotParam.toLowerCase() === 'true'
  const urlSelectedLayer = getUrlParamAsNumber('selectedLayer')
  
  // Calculate project extent and zoom using shared utilities.
  // Memoized so consumers (e.g. MapView effects) get a stable reference across
  // unrelated parent re-renders — otherwise overlays would be torn down on
  // every render, hiding the map-click probe popup as soon as it appears.
  const projectExtent = React.useMemo(
    () => calculateProjectExtent(projectSource),
    [projectSource]
  )
  const projectExtentShape = React.useMemo(() => ({
    value: projectExtent,
    shape: projectSource.shape,
    layer: projectSource.layer,
    cql: projectSource.cql,
    externalLink: projectSource.externalLink,
  }), [projectExtent, projectSource.shape, projectSource.layer, projectSource.cql, projectSource.externalLink])
  
  const [project, setProject] = React.useState<Project>({ ...defaultProject, ...projectSource })
  const [sidebarVisible, setSidebarVisible] = React.useState(urlSidebar)
  
  // Get the top layer (first in the reversed display order)
  const getTopLayerId = React.useCallback(() => {
    const allLayers = Array.from(project.allLayers)
    if (allLayers.length === 0) return undefined
    // Layers are displayed in reverse order, so the first one in the reversed array is the top
    return allLayers[allLayers.length - 1]
  }, [project.allLayers])
  
  // Determine initial selected layer: URL parameter > top layer > undefined
  const initialSelectedLayer = React.useMemo(() => {
    if (urlSelectedLayer !== null) {
      // Check if the URL parameter layer ID exists in the project
      const allLayers = Array.from(project.allLayers)
      if (allLayers.includes(urlSelectedLayer)) {
        return urlSelectedLayer
      }
    }
    // Default to top layer
    return getTopLayerId()
  }, [urlSelectedLayer, project.allLayers, getTopLayerId])
  
  const [selectedLayer, setSelectedLayer] = React.useState<number | undefined>(initialSelectedLayer)
  const [mapLayerStackEmbed, setMapLayerStackEmbed] = React.useState<MapStackSlot[]>([])
  const [localGeotiffLayersEmbed, setLocalGeotiffLayersEmbed] = React.useState<Record<string, LocalGeotiffLayerState>>({})
  const [selectedLocalGeotiffIdEmbed, setSelectedLocalGeotiffIdEmbed] = React.useState<string | null>(null)
  const [showAP, setShowAP] = React.useState(urlSnapshot) // Show analysis panel if snapshot parameter is set
  const [settingsCollapsed, setSettingsCollapsed] = React.useState(false)
  /** Generous padding + maxZoom cap so embed doesn’t fit tighter than the main editor */
  const embedExtentFitPadding = React.useMemo((): [number, number, number, number] => {
    const p = 160
    return [p, p, p, p]
  }, [])
  const [selectedArea, setSelectedArea] = React.useState<Extent | null>(null)
  const [extentShape, setExtentShape] = React.useState<SnapshotExtentData | undefined>(undefined)
  
  // Update selected layer when project layers change (to maintain top layer selection)
  React.useEffect(() => {
    const allLayers = Array.from(project.allLayers)
    if (selectedLayer === undefined || !allLayers.includes(selectedLayer)) {
      const topLayer = getTopLayerId()
      if (topLayer !== undefined) {
        setSelectedLayer(topLayer)
      }
    }
  }, [project.allLayers, selectedLayer, getTopLayerId])
  
  // Set selected area to project extent if snapshot tool is enabled via URL parameter
  React.useEffect(() => {
    if (urlSnapshot && selectedArea === null) {
      setSelectedArea(projectExtent)
      setExtentShape(projectExtentShape)
      // Ensure a layer is selected when opening snapshot tool
      if (selectedLayer === undefined) {
        const topLayer = getTopLayerId()
        if (topLayer !== undefined) {
          setSelectedLayer(topLayer)
        }
      }
    }
  }, [urlSnapshot, projectExtent, projectExtentShape, selectedArea, selectedLayer, getTopLayerId])
  const projectZoom = calculateProjectZoom(projectExtent)
  const { mask: projectMask, maskSource: projectMaskSource, maskCQL: projectMaskCQL } = extractProjectMaskSettings(projectSource)
  
  // Parse center coordinates from URL if provided
  const defaultCenter = calculateProjectCenter(projectExtent)
  const initialCenter: [number, number] = urlCenter
    ? (() => {
        const coords = urlCenter.split(',').map(c => parseFloat(c.trim()))
        if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
          return [coords[0], coords[1]] as [number, number]
        }
        return defaultCenter
      })()
    : defaultCenter
  
  const [mapViewZoom, setMapViewZoom] = React.useState(urlZoom ?? 9)
  const [mapViewCenter, setMapViewCenter] = React.useState<[number, number]>(initialCenter)
  const [datasetOutputCache, setDatasetOutputCache] = React.useState<DatasetCache>({})
  const [loadingQueue, setLoadingQueue] = React.useState<number[]>([])
  const [modelOutputCache, setModelOutputCache] = React.useState<ModelOutputCache>({})
  // Check if there's a model to process - just check if model exists, let ModelView handle validation
  const hasModel = project.model !== null && project.model !== undefined
  // Only start processing if autostart is enabled (default true) and model exists
  const shouldAutostart = urlAutostart && hasModel
  const [isProcessing, setProcessing] = React.useState(shouldAutostart) // Start with processing state only if autostart enabled
  const [process, setProcess] = React.useState(false) // Don't trigger immediately - wait for editor to be ready
  const [modelViewTransform, setModelViewTransform] = React.useState({ x: 0, y: 0, k: 1 })
  const [extraInfo, setExtraInfo] = React.useState<string | null>(null)
  const [editorReady, setEditorReady] = React.useState(false)
  const isDatasetLoading = loadingQueue.length > 0
  const showLoadingOverlay = isProcessing || isDatasetLoading

  // Trigger processing once editor is ready (give it a moment to initialize)
  React.useEffect(() => {
    if (shouldAutostart && !editorReady && !process) {
      // Wait a bit for the editor to initialize, then trigger processing
      const timer = setTimeout(() => {
        if (urlDebug) {
          console.log('Embed: Editor should be ready, triggering process')
        }
        setEditorReady(true)
        setProcess(true)
        // Also ensure processing state is set
        setProcessing(true)
      }, urlInitDelay) // Use URL parameter or default delay
      return () => clearTimeout(timer)
    }
  }, [shouldAutostart, editorReady, process, urlInitDelay, urlDebug])

  // Add a timeout fallback in case processing gets stuck or never starts
  React.useEffect(() => {
    if (hasModel) {
      // If processing hasn't started after 5 seconds, something is wrong
      const startTimeout = setTimeout(() => {
        if (isProcessing && !process) {
          if (urlDebug) {
            console.warn('Embed: Processing never started - showing map anyway')
          }
          setProcessing(false)
        }
      }, 5000)
      
      // If processing is taking too long, show map anyway (use URL parameter or default)
      const processingTimeout = setTimeout(() => {
        if (isProcessing) {
          if (urlDebug) {
            console.warn('Embed: Model processing timeout - showing map anyway')
          }
          setProcessing(false)
          setProcess(false)
        }
      }, urlTimeout) // Use URL parameter or default timeout
      
      return () => {
        clearTimeout(startTimeout)
        clearTimeout(processingTimeout)
      }
    } else {
      // No model, ensure we're not stuck in processing state
      if (isProcessing) {
        setProcessing(false)
      }
    }
  }, [isProcessing, hasModel, process, urlTimeout, urlDebug])

  // Wrapper for setProcessing that includes error handling
  const setProcessingWithErrorHandling = React.useCallback((processing: boolean) => {
    if (urlDebug) {
      console.log('Embed: setProcessing called with', processing)
    }
    setProcessing(processing)
    if (!processing) {
      // Processing completed, reset process flag
      setProcess(false)
    }
  }, [urlDebug])

  // Debug logging (only if debug mode enabled)
  React.useEffect(() => {
    if (urlDebug) {
      console.log('Embed: hasModel', hasModel, 'isProcessing', isProcessing, 'process', process, 'editorReady', editorReady, 'model:', project.model)
      if (project.model) {
        console.log('Embed: model structure:', {
          hasNodes: !!(project.model as any).nodes,
          nodesLength: (project.model as any).nodes?.length,
          hasConnections: !!(project.model as any).connections,
          connectionsLength: (project.model as any).connections?.length
        })
      }
    }
  }, [hasModel, isProcessing, process, editorReady, project.model, urlDebug])

  // Load datasets when needed
  const loadDataset = React.useCallback((layer: DatasetLayer) => {
    if (layer.deleted) return
    
    setLoadingQueue(prev => {
      if (prev.includes(layer.id)) return prev
      return [...prev, layer.id]
    })
    
    getDataset(layer.id, teamId, (err, out, colorMetadata) => {
      if (out && !err) {
        setDatasetOutputCache(prev => ({ ...prev, [layer.id]: out }))
        
        if (colorMetadata && (colorMetadata.colors || colorMetadata.fill)) {
          setProject(prev => {
            const layerId = Array.from(prev.allLayers).find(id => {
              const l = prev.layers[id]
              return l.type === "DatasetLayer" && l.id === layer.id
            })
            
            if (layerId !== undefined) {
              const updates: Partial<DatasetLayer> = {}
              if (colorMetadata.colors) updates.colors = colorMetadata.colors
              if (colorMetadata.fill) updates.fill = colorMetadata.fill
              const updatedLayer = { ...prev.layers[layerId], ...updates } as Layer
              return {
                ...prev,
                layers: {
                  ...prev.layers,
                  [layerId]: updatedLayer
                }
              }
            }
            return prev
          })
        }
      }
      setLoadingQueue(prev => prev.filter(id => id !== layer.id))
    })
  }, [teamId])
  
  // Load dataset when a DatasetLayer is selected
  React.useEffect(() => {
    if (selectedLayer !== undefined) {
      const layer = project.layers[selectedLayer]
      if (layer.type === "DatasetLayer") {
        const datasetLayer = layer as DatasetLayer
        // Check if dataset is already loaded
        if (!datasetOutputCache[datasetLayer.id] && !loadingQueue.includes(datasetLayer.id)) {
          loadDataset(datasetLayer)
        }
      }
    }
  }, [selectedLayer, project.layers, datasetOutputCache, loadingQueue, loadDataset])

  const projectAllLayersKeyEmbed = project.allLayers.join(',')
  const mapLayerStackEmbedReconciled = React.useMemo(
    () => reconcileMapStackWithProject(mapLayerStackEmbed, project.allLayers),
    [mapLayerStackEmbed, projectAllLayersKeyEmbed],
  )
  /** Top-first list for sidebar; only slots with resolvable layer data (keeps ReactSortable in sync). */
  const embedLayersDisplayStack = React.useMemo(() => {
    return mapLayerStackEmbedReconciled
      .slice()
      .reverse()
      .filter(slot => {
        if (slot.kind === "project") {
          return project.layers[slot.layerId] !== undefined
        }
        return localGeotiffLayersEmbed[slot.localId] !== undefined
      })
  }, [mapLayerStackEmbedReconciled, project.layers, localGeotiffLayersEmbed])

  /**
   * Map render order: hoist the currently selected layer to the top of the draw
   * order without changing the sidebar's stored order. Stack is bottom→top, so
   * the selected slot is moved to the end of the array.
   */
  const effectiveMapLayerStackEmbed = React.useMemo(() => {
    const isSelected = (slot: MapStackSlot) => {
      if (selectedLocalGeotiffIdEmbed !== null) {
        return slot.kind === "localGeotiff" && slot.localId === selectedLocalGeotiffIdEmbed
      }
      if (selectedLayer !== undefined) {
        return slot.kind === "project" && slot.layerId === selectedLayer
      }
      return false
    }
    const idx = mapLayerStackEmbedReconciled.findIndex(isSelected)
    if (idx === -1 || idx === mapLayerStackEmbedReconciled.length - 1) {
      return mapLayerStackEmbedReconciled
    }
    const next = mapLayerStackEmbedReconciled.slice()
    const [chosen] = next.splice(idx, 1)
    next.push(chosen)
    return next
  }, [mapLayerStackEmbedReconciled, selectedLayer, selectedLocalGeotiffIdEmbed])
  React.useEffect(() => {
    setMapLayerStackEmbed(prev => {
      if (prev.length === 0 && project.allLayers.length > 0) {
        return project.allLayers.map(layerId => ({ kind: 'project' as const, layerId }))
      }
      return reconcileMapStackWithProject(prev, project.allLayers)
    })
  }, [projectAllLayersKeyEmbed])

  const onLocalGeotiffParsedEmbed = React.useCallback((items: LocalGeotiffLayerState[]) => {
    setLocalGeotiffLayersEmbed(prev => {
      const next = { ...prev }
      for (const it of items) {
        next[it.id] = it
      }
      return next
    })
    setMapLayerStackEmbed(prev => [...prev, ...items.map(it => ({ kind: 'localGeotiff' as const, localId: it.id }))])
  }, [])

  const mutateEmbedLocalGeotiff = React.useCallback((id: string, data: Partial<Pick<LocalGeotiffLayerState, 'name' | 'opacity' | 'visible'>>) => {
    setLocalGeotiffLayersEmbed(prev => {
      const cur = prev[id]
      if (!cur) {
        return prev
      }
      return { ...prev, [id]: { ...cur, ...data } }
    })
  }, [])

  const removeEmbedLocalGeotiff = React.useCallback((id: string) => {
    setLocalGeotiffLayersEmbed(prev => {
      const { [id]: _removed, ...rest } = prev
      return rest
    })
    setMapLayerStackEmbed(prev => prev.filter(s => !(s.kind === 'localGeotiff' && s.localId === id)))
    setSelectedLocalGeotiffIdEmbed(cur => (cur === id ? null : cur))
  }, [])

  const embedProjectLayersRef = React.useRef(project.allLayers)
  embedProjectLayersRef.current = project.allLayers

  const reorderEmbedLayers = React.useCallback((slots: MapStackSlot[]) => {
    const order = projectOrderFromStack(slots)
    if (!isSameLayerIdMultiset(embedProjectLayersRef.current, order)) {
      return
    }
    setMapLayerStackEmbed(slots)
    setProject(prev => ({ ...prev, allLayers: order }))
  }, [])

  const mutateLayer = (id: number, data: Partial<Layer>) => {
    setProject(prev => {
      const updatedLayer = { ...prev.layers[id], ...data } as Layer
      return {
        ...prev,
        layers: {
          ...prev.layers,
          [id]: updatedLayer
        }
      }
    })
  }

  // Create output layer callback
  const createOutputLayer = React.useCallback((nodeId: number) => {
    setProject(prev => {
      const newId = Math.max(...Array.from(prev.allLayers), 0) + 1
      return {
        ...prev,
        allLayers: [...prev.allLayers, newId],
        layers: {
          ...prev.layers,
          [newId]: {
            name: "Untitled model output",
            visible: true,
            opacity: 1,
            type: "ModelOutputLayer",
            nodeId,
            fill: "greyscale",
            colors: [],
            overrideBounds: false,
            bounds: undefined
          }
        }
      }
    })
  }, [])
  
  // Save map layer callback
  const saveMapLayer = React.useCallback((id: number, name: string | undefined, tileGrid: any) => {
    setModelOutputCache(prev => ({ ...prev, [id]: tileGrid }))
    if (name) {
      setProject(prev => {
        const layerId = Array.from(prev.allLayers).find(lid => {
          const l = prev.layers[lid]
          return l.type === "ModelOutputLayer" && l.nodeId === id
        })
        if (layerId !== undefined) {
          return {
            ...prev,
            layers: {
              ...prev.layers,
              [layerId]: { ...prev.layers[layerId], name }
            }
          }
        }
        return prev
      })
    }
  }, [])

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Hidden ModelView to process the model - only render if model exists */}
      {hasModel && (
        <div style={{ position: "absolute", visibility: "hidden", width: 0, height: 0, overflow: "hidden" }}>
          <ModelView
            visible={false}
            initialTransform={modelViewTransform}
            setTransform={setModelViewTransform}
            initialModel={project.model}
            setModel={(model) => {
              if (urlDebug) {
                console.log('Embed: Model updated', model)
              }
              setProject(prev => ({ ...prev, model }))
            }}
            createOutputLayer={(nodeId) => {
              if (urlDebug) {
                console.log('Embed: Creating output layer for node', nodeId)
              }
              createOutputLayer(nodeId)
            }}
            deleteOutputLayer={() => {}}
            getOutputLayerStyle={(nodeId) => {
              const layer = Object.values(project.layers).find(
                l => l.type === "ModelOutputLayer" && l.nodeId === nodeId
              )
              if (!layer || layer.type !== "ModelOutputLayer") return null
              return {
                fill: layer.fill,
                colors: layer.colors,
                colorByLabel: layer.colorByLabel,
              }
            }}
            mutateOutputLayer={(nodeId, data) => {
              setProject(prev => {
                const layerId = Object.keys(prev.layers)
                  .map(Number)
                  .find(id => {
                    const l = prev.layers[id]
                    return l.type === "ModelOutputLayer" && l.nodeId === nodeId
                  })
                if (layerId === undefined) return prev
                const layer = prev.layers[layerId]
                if (layer.type !== "ModelOutputLayer") return prev
                return {
                  ...prev,
                  layers: {
                    ...prev.layers,
                    [layerId]: { ...layer, ...data } as typeof layer,
                  },
                }
              })
              return true
            }}
            saveMapLayer={(id, name, tileGrid) => {
              if (urlDebug) {
                console.log('Embed: Saving map layer', id, name)
              }
              saveMapLayer(id, name, tileGrid)
            }}
            setProcessing={(processing) => {
              if (urlDebug) {
                console.log('Embed: setProcessing called from ModelView', processing)
              }
              setProcessingWithErrorHandling(processing)
            }}
            autoProcessing={false}
            process={process}
            setProcess={(p) => {
              if (urlDebug) {
                console.log('Embed: setProcess called', p)
              }
              setProcess(p)
            }}
            saveModel={(name: string, json: any, callback?: (status: number) => void) => saveModelOutput(name, json, teamId, callback)}
            getDatasets={() => getDatasets(teamId)}
            getAnnotations={() => getAnnotations(teamId)}
            extent={projectExtent}
            zoom={projectZoom}
            mask={projectMask}
            maskLayer={projectMaskSource}
            maskCQL={projectMaskCQL}
            maskShape={projectSource.shape}
            permissions={permissions}
            getCustomExpressions={() => getExpressions(teamId)}
            debug={urlDebug ?? false}
            extentList={teamExtents.map((extent) => ({ name: extent.name, id: extent.id, level: extent.level ?? null }))}
            externalLink={projectSource.externalLink}
            setExtraInfo={(info) => {
              if (urlDebug) {
                console.log('Embed: Extra info', info)
              }
              setExtraInfo(info)
            }}
            teamName={teamName}
            teamId={teamId}
            teamComponents={teamComponents}
          />
        </div>
      )}
      
      {/* Loading screen while processing */}
      {showLoadingOverlay && (
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000,
        }}>
          <img
            src={embedUosLogo}
            alt=""
            style={{
              position: "absolute",
              top: "1rem",
              right: "1rem",
              height: "40px",
              width: "auto",
              objectFit: "contain",
            }}
          />
          <div style={{ textAlign: "center", maxWidth: "90%" }}>
            <img
              src={embedLoadingIcon}
              alt=""
              className="embed-loading-icon--animated"
              style={{
                display: "block",
                margin: "0 auto 0.5rem",
                maxWidth: "min(200px, 40vw)",
                height: "auto",
              }}
            />
            <h1 className="embed-loading-title">LIMMMA</h1>
            <div className="embed-loading-subtext" aria-busy="true">
              <span>{isDatasetLoading && !isProcessing ? "loading datasets" : "loading"}</span>
              <span className="embed-loading-dots-clip" aria-hidden>
                <span className="embed-loading-dots-clip-inner">...</span>
              </span>
            </div>
            {extraInfo ? (
              <div style={{ fontSize: "0.9em", color: "#666", marginTop: "0.45rem" }}>
                {extraInfo}
              </div>
            ) : null}
          </div>
        </div>
      )}
      
      {/* Map | unified sidebar (snapshot above layers when both) | collapsed strip */}
      {!showLoadingOverlay && (
        <div className="flex-grow-1 d-flex" style={{ minHeight: 0, minWidth: 0 }}>
          <div className="flex-grow-1 position-relative d-flex flex-column" style={{ minWidth: 0, minHeight: 0 }}>
            <div className="flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
              <MapView
                projectLayersById={project.layers}
                mapLayerStack={effectiveMapLayerStackEmbed}
                localGeotiffLayers={localGeotiffLayersEmbed}
                onLocalGeotiffParsed={onLocalGeotiffParsedEmbed}
                dbModels={dbModels}
                initialZoom={mapViewZoom}
                setZoom={setMapViewZoom}
                initialCenter={mapViewCenter}
                setCenter={setMapViewCenter}
                modelOutputCache={modelOutputCache}
                datasetCache={datasetOutputCache}
                loadTeamDataset={loadDataset}
                setSelectedArea={setSelectedArea}
                selectedArea={selectedArea}
                selected={showAP}
                setSelected={setShowAP}
                projectExtent={projectExtent}
                showProjectExtent={false}
                extentShape={extentShape}
                setExtentShape={setExtentShape}
                projectExtentShape={projectExtentShape}
                skipFitToExtent={urlZoom != null || urlCenter != null}
                showScaleLine={false}
                extentFitPadding={embedExtentFitPadding}
                extentFitMaxZoom={12}
                extentFitRetryMs={450}
                teamId={teamId}
                model={project.model}
              />
            </div>
            {selectedLayer !== undefined && (project.layers[selectedLayer]?.type === "ModelOutputLayer" || project.layers[selectedLayer]?.type === "DatasetLayer") && (() => {
              const legendLayer = project.layers[selectedLayer] as ModelOutputLayer | DatasetLayer
              return (
              <LegendDisplay
                key={`legend-${selectedLayer}-${legendLayer.colors?.length || 0}-${Object.keys(legendLayer.colorByLabel ?? {}).length}-${legendLayer.categoricalView ?? "classes"}`}
                layer={legendLayer}
                model={project.model}
                modelOutputCache={modelOutputCache}
                datasetOutputCache={datasetOutputCache}
                loadingQueue={loadingQueue}
                mutateColors={styleUpdate => mutateLayer(selectedLayer, styleUpdate)}
              />
              )
            })()}
          </div>
          {(sidebarVisible || showAP) ? (
            <div
              className="d-flex flex-column bg-light border-left small flex-shrink-0"
              style={{
                width: EMBED_SIDE_COLUMN_WIDTH_PX,
                minHeight: 0,
                alignSelf: "stretch",
                overflow: "hidden",
                fontSize: "0.8125rem",
                lineHeight: 1.35,
              }}
            >
              {showAP && (
                <div
                  className="d-flex flex-column"
                  style={{
                    flex: sidebarVisible ? "1 1 52%" : "1 1 auto",
                    minHeight: sidebarVisible ? 120 : 0,
                    overflow: "hidden",
                    borderBottom: sidebarVisible ? "1px solid #dee2e6" : undefined,
                  }}
                >
                  <AnalysisPanel
                    setShowAP={() => setShowAP(false)}
                    selectedArea={selectedArea}
                    setSelectedArea={setSelectedArea}
                    selectedLayer={selectedLayer !== undefined ? project.layers[selectedLayer] : null}
                    layerStats={(layer: ModelOutputLayer | DatasetLayer) => {
                      const cache = layer.type === "ModelOutputLayer" ? modelOutputCache : datasetOutputCache
                      const id = layer.type === "ModelOutputLayer" ? layer.nodeId : layer.id
                      return cache[id] ? cache[id] : null
                    }}
                    currentTab={Tab.MapView}
                    projectExtent={projectExtent}
                    ExtentList={teamExtents}
                    extentShape={extentShape}
                    setExtentShape={setExtentShape}
                    projectExtentShape={projectExtentShape}
                    hideCoordinates={sidebarVisible}
                    columnWidth={EMBED_SIDE_COLUMN_WIDTH_PX}
                    embedStacked
                    model={project.model}
                    projectLayers={project.layers}
                    layerOrder={project.allLayers}
                    selectedLayerId={selectedLayer}
                  />
                </div>
              )}
              {sidebarVisible && (
                <div
                  className="d-flex flex-column flex-grow-1"
                  style={{ minHeight: 0, flex: showAP ? "1 1 48%" : "1 1 auto" }}
                >
              <div className="px-2 py-1 border-bottom d-flex align-items-center bg-light flex-shrink-0" style={{ fontWeight: 600 }}>
                <div className="flex-grow-1">Layers</div>
                <i className="ml-1 fas fa-angle-double-right" style={{ cursor: "pointer", fontSize: "0.9em" }} onClick={() => setSidebarVisible(false)} />
              </div>
              <div
                className="flex-grow-1 bg-white"
                style={{ overflowY: "auto", flexBasis: "0px", minHeight: 0 }}
                onClick={() => {
                  setSelectedLayer(undefined)
                  setSelectedLocalGeotiffIdEmbed(null)
                }}
              >
                <ReactSortable
                  list={embedLayersDisplayStack.map(slot => ({ id: slotToKey(slot) }))}
                  setList={(list: { id: string }[]) => {
                    const nextSlots = list.map(item => keyToSlot(item.id)).reverse()
                    reorderEmbedLayers(nextSlots)
                  }}
                  animation={150}
                  delay={80}
                  delayOnTouchOnly
                  className="embed-layers-sortable"
                >
                  {embedLayersDisplayStack.map(slot => {
                    if (slot.kind === "project") {
                      const id = slot.layerId
                      const layer = project.layers[id]
                      if (layer === undefined) {
                        return null
                      }
                      const iconColor = layer.type === "ShapeLayer"
                        ? `rgb(${layer.colors?.fill?.join(',') || '96,96,96'})`
                        : "rgb(96,96,96)"
                      return (
                        <div key={slotToKey(slot)} className="embed-layer-sortable-item">
                          <LayerListRow
                            name={layer.name}
                            iconClassName={iconForLayerType(layer.type)}
                            iconColor={iconColor}
                            visible={Boolean(layer.visible)}
                            selected={selectedLocalGeotiffIdEmbed === null && id === selectedLayer}
                            onSelect={(e) => {
                              e.stopPropagation()
                              setSelectedLocalGeotiffIdEmbed(null)
                              setSelectedLayer(id === selectedLayer ? undefined : id)
                            }}
                            onToggleVisibility={(e) => {
                              e.stopPropagation()
                              mutateLayer(id, { visible: !layer.visible })
                            }}
                          />
                        </div>
                      )
                    }

                    const localRow = localGeotiffLayersEmbed[slot.localId]
                    if (!localRow) {
                      return null
                    }
                    const lid = slot.localId
                    return (
                      <div key={slotToKey(slot)} className="embed-layer-sortable-item">
                        <LayerListRow
                          name={localRow.name}
                          iconClassName="fa-image"
                          iconColor="rgb(96,96,96)"
                          visible={localRow.visible !== false}
                          selected={lid === selectedLocalGeotiffIdEmbed}
                          title="Local GeoTIFF — not saved with the project"
                          onSelect={(e) => {
                            e.stopPropagation()
                            setSelectedLayer(undefined)
                            setSelectedLocalGeotiffIdEmbed(cur => (cur === lid ? null : lid))
                          }}
                          onToggleVisibility={(e) => {
                            e.stopPropagation()
                            mutateEmbedLocalGeotiff(lid, { visible: !localRow.visible })
                          }}
                          deleteTitle="Remove preview"
                          onDelete={(e) => {
                            e.stopPropagation()
                            removeEmbedLocalGeotiff(lid)
                          }}
                        />
                      </div>
                    )
                  })}
                </ReactSortable>
              </div>
              {(selectedLayer !== undefined || (selectedLocalGeotiffIdEmbed !== null && localGeotiffLayersEmbed[selectedLocalGeotiffIdEmbed])) && (
                <>
                <LayerSettingsHeader
                  compact
                  collapsed={settingsCollapsed}
                  onToggle={() => setSettingsCollapsed(collapsed => !collapsed)}
                  valueLabel={
                    selectedLocalGeotiffIdEmbed !== null && localGeotiffLayersEmbed[selectedLocalGeotiffIdEmbed]
                      ? `opacity:${Math.round(localGeotiffLayersEmbed[selectedLocalGeotiffIdEmbed].opacity * 100)}%`
                      : selectedLayer !== undefined
                        ? `opacity:${Math.round((project.layers[selectedLayer].opacity || 1) * 100)}%`
                        : undefined
                  }
                />
              {!settingsCollapsed && selectedLayer !== undefined && selectedLocalGeotiffIdEmbed === null && (
                <div className="px-2 py-2 bg-white flex-shrink-0" style={{ minWidth: 0 }}>
                  <div
                    className="d-flex align-items-center mt-0"
                    style={{ gap: 6, minWidth: 0, fontSize: "0.72rem" }}
                  >
                    <span style={{ flexShrink: 0 }}>Opacity</span>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={project.layers[selectedLayer].opacity || 1}
                      onChange={e => mutateLayer(selectedLayer, { opacity: parseFloat(e.target.value) })}
                      className="custom-range"
                      style={{
                        minHeight: "1.05rem",
                        minWidth: 0,
                        flex: "1 1 0",
                        width: 0,
                        margin: 0,
                      }}
                    />
                    <span style={{ flexShrink: 0, minWidth: "2em", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {Math.round((project.layers[selectedLayer].opacity || 1) * 100)}%
                    </span>
                  </div>
                </div>
              )}
              {!settingsCollapsed && selectedLocalGeotiffIdEmbed !== null && localGeotiffLayersEmbed[selectedLocalGeotiffIdEmbed] && (
                <div className="px-2 py-2 bg-white flex-shrink-0" style={{ minWidth: 0, fontSize: "0.72rem" }}>
                  <p className="small text-muted mb-2" style={{ lineHeight: 1.3 }}>
                    Local GeoTIFF — not saved with the embedded project view.
                  </p>
                  <div
                    className="d-flex align-items-center mb-2"
                    style={{ gap: 6, minWidth: 0 }}
                  >
                    <span style={{ flexShrink: 0 }}>Opacity</span>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={localGeotiffLayersEmbed[selectedLocalGeotiffIdEmbed].opacity}
                      onChange={e =>
                        mutateEmbedLocalGeotiff(selectedLocalGeotiffIdEmbed, { opacity: parseFloat(e.target.value) })
                      }
                      className="custom-range"
                      style={{
                        minHeight: "1.05rem",
                        minWidth: 0,
                        flex: "1 1 0",
                        width: 0,
                        margin: 0,
                      }}
                    />
                    <span style={{ flexShrink: 0, minWidth: "2em", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {Math.round(localGeotiffLayersEmbed[selectedLocalGeotiffIdEmbed].opacity * 100)}%
                    </span>
                  </div>
                </div>
              )}
                </>
              )}
                </div>
              )}
            </div>
          ) : (
            <CollapsedSidebar compact show={() => setSidebarVisible(true)} />
          )}
        </div>
      )}
    </div>
  )
}

// Separate component to ensure it re-renders when layer colors change
function normalizeLegendColor(
  color: unknown,
  fallback: [number, number, number, number]
): [number, number, number, number] {
  if (Array.isArray(color) && color.length >= 4) {
    return [Number(color[0]), Number(color[1]), Number(color[2]), Number(color[3])]
  }
  return fallback
}

function LegendDisplay({ layer, model, modelOutputCache, datasetOutputCache, loadingQueue, mutateColors }: {
  layer: ModelOutputLayer | DatasetLayer
  model: Data | null
  modelOutputCache: ModelOutputCache
  datasetOutputCache: DatasetCache
  loadingQueue: number[]
  mutateColors: (style: Pick<ModelOutputLayer | DatasetLayer, "colors" | "colorByLabel">) => void
}) {
  const [legendCollapsed, setLegendCollapsed] = React.useState(false)

  const legendShellStyle: React.CSSProperties = {
    position: "absolute",
    bottom: "10px",
    left: "10px",
    zIndex: 1001,
    backgroundColor: "rgba(255, 255, 255, 0.75)",
    padding: "6px 8px",
    borderRadius: "3px",
    boxShadow: "0 1px 5px rgba(0,0,0,0.12)",
    maxWidth: "min(560px, 92vw)",
    fontSize: "0.82em",
    backdropFilter: "blur(6px)",
  }

  const wrapLegend = (body: React.ReactNode) => (
    <div style={legendShellStyle}>
      <div className="d-flex align-items-center justify-content-between" style={{ gap: 6 }}>
        <div
          style={{
            fontWeight: 800,
            fontSize: "0.78em",
            letterSpacing: "-0.02em",
            lineHeight: 1.15,
            color: "#0a0e14",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          {layer.name}
        </div>
        <button
          type="button"
          className="btn btn-sm btn-link embed-legend-collapse-btn flex-shrink-0 text-dark"
          style={{ lineHeight: 1 }}
          aria-expanded={!legendCollapsed}
          aria-label={legendCollapsed ? "Expand legend" : "Collapse legend"}
          onClick={() => setLegendCollapsed(c => !c)}
        >
          <i className={`fas fa-fw fa-chevron-${legendCollapsed ? "up" : "down"}`} />
        </button>
      </div>
      {!legendCollapsed && <div style={{ marginTop: 4 }}>{body}</div>}
    </div>
  )

  const layerColors = layer.colors

  const cache = layer.type === "ModelOutputLayer" ? modelOutputCache : datasetOutputCache
  const id = layer.type === "ModelOutputLayer" ? layer.nodeId : layer.id
  const tileGrid = cache[id]

  if (!tileGrid) return wrapLegend(<div style={{ color: "#666", fontSize: "0.88em" }}>Loading...</div>)

  const rawStats = tileGrid.getStats()

  if (rawStats.type === undefined) {
    return wrapLegend(<div style={{ color: "#666", fontSize: "0.88em" }}>Loading...</div>)
  }

  const stats = effectiveLegendStatsForLayer(layer, tileGrid, rawStats, model)

  const labelEntries = rawStats.type === "CategoricalTileGrid"
    ? labelEntriesFromStatsLabels(rawStats.labels)
    : []
  const maxKey = labelEntries.reduce((max, entry) => Math.max(max, entry.key), stats.max ?? 0)

  let colors: any[] = []
  if (stats.type === "NumericTileGrid") {
    colors = getColorStops((layer.fill == "greyscale" ? "greys" : (layer.fill === "heatmap" ? "jet" : layer.fill)), 50)
      .filter(c => typeof c !== "number")
      .reverse()
  } else if (stats.type === "CategoricalTileGrid") {
    colors = resolveCategoricalPalette(
      labelEntries,
      maxKey,
      { colors: layer.colors, colorByLabel: layer.colorByLabel },
      index => layerColors?.[index] ?? [0, 0, 0, 0]
    )

    if (layer.type === "DatasetLayer" &&
        (!colors || colors.length === 0) &&
        loadingQueue.includes(layer.id)) {
      return wrapLegend(<div style={{ color: "#666", fontSize: "0.88em" }}>Loading colors...</div>)
    }
  } else {
    colors = layerColors || []

    if (stats.type === "BooleanTileGrid" &&
        layer.type === "DatasetLayer" &&
        (!colors || colors.length === 0) &&
        loadingQueue.includes(layer.id)) {
      return wrapLegend(<div style={{ color: "#666", fontSize: "0.88em" }}>Loading colors...</div>)
    }
  }

  if (!colors || colors.length === 0) {
    if (layer.type === "DatasetLayer" && loadingQueue.includes(layer.id)) {
      return wrapLegend(<div style={{ color: "#666", fontSize: "0.88em" }}>Loading colors...</div>)
    }
    return wrapLegend(<div style={{ color: "#666", fontSize: "0.88em" }}>No colors available</div>)
  }

  const legendBody = (() => {
    if (stats.type === "BooleanTileGrid") {
      const data = [{ color: colors[0] || [128, 128, 128, 1], label: "False" }, { color: colors[1] || [255, 255, 255, 1], label: "True" }]
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {data.map(({ color, label }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{ 
                width: "14px", 
                height: "14px", 
                backgroundColor: `rgb(${color[0]}, ${color[1]}, ${color[2]})`,
                border: "1px solid #ccc",
                flexShrink: 0
              }} />
              <span style={{ fontSize: "0.75em", lineHeight: 1.25 }}>{label}</span>
            </div>
          ))}
        </div>
      )
    }
    if (rawStats.type === "CategoricalTileGrid" && rawStats.labels && stats.type === "CategoricalTileGrid") {
      const labels = rawStats.labels
      const maxLabelIndex = labels.reduce((max: number, obj: { name: number }) => Math.max(max, Number(obj.name) || 0), 0)
      const safeColors: [number, number, number, number][] = Array.from({ length: maxLabelIndex }, (_, index) => {
        return normalizeLegendColor(colors?.[index], [0, 0, 0, 0])
      })

      const setCategoryVisibility = (key: number, visible: boolean) => {
        const updatedColors = safeColors.map((color, index) => {
          if (index + 1 === key || key === -1) {
            return [color[0], color[1], color[2], visible ? 1 : 0] as [number, number, number, number]
          }
          return color
        })
        mutateColors(
          applyCategoricalColorMutation(
            labelEntriesFromStatsLabels(labels),
            maxLabelIndex,
            updatedColors,
            { colors: layer.colors, colorByLabel: layer.colorByLabel },
            true
          )
        )
      }

      const toggleCategory = (key: number) => {
        const color = safeColors[key - 1] ?? [0, 0, 0, 0]
        setCategoryVisibility(key, color[3] !== 1)
      }

      const data = labels.map((obj: { name: number; value: string }) => ({
        label: obj.value,
        color: safeColors[obj.name - 1] ?? [0, 0, 0, 0],
        key: obj.name
      }))

      const allSelected = safeColors.every(c => c[3] === 1)

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "3px", maxHeight: "168px", overflowY: "auto" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.75em", lineHeight: 1.25, cursor: "pointer" }}>
            <input type="checkbox" checked={allSelected} onChange={event => setCategoryVisibility(-1, event.target.checked)} />
            <span>Select/Unselect All</span>
          </label>
          {data.map(({ color, label, key }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.75em", lineHeight: 1.25 }}>
              <button
                type="button"
                title={color[3] ? `Hide ${label}` : `Show ${label}`}
                aria-pressed={!!color[3]}
                onClick={() => toggleCategory(key)}
                style={{
                  width: "14px",
                  height: "14px",
                  padding: 0,
                  backgroundColor: `rgb(${color[0]}, ${color[1]}, ${color[2]})`,
                  border: "1px solid #ccc",
                  flexShrink: 0,
                  opacity: color[3] ? 1 : 0.35,
                  cursor: "pointer",
                }}
              />
              <span style={{ opacity: color[3] ? 1 : 0.55 }}>{label}</span>
            </div>
          ))}
        </div>
      )
    }
    const min = stats.min
    const max = stats.max
    return (
      <div>
        <div style={{ 
          display: "flex", 
          height: "20px", 
          borderRadius: "2px",
          overflow: "hidden",
          marginBottom: "4px"
        }}>
          {colors.map((color: any) => (
            <div
              key={color.join(",")}
              style={{ 
                flex: 1,
                backgroundColor: `rgb(${color.join(",")})`,
              }}
            />
          ))}
        </div>
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between",
          fontSize: "0.85em",
          color: "#666"
        }}>
          <span>{isFinite(min) ? min.toFixed(2) : "N/A"}</span>
          <span>{isFinite(max) ? max.toFixed(2) : "N/A"}</span>
        </div>
      </div>
    )
  })()

  return wrapLegend(legendBody)
}
